// DataStorageSection.tsx — Settings → Data & Storage. Every number and button is
// REAL (see storageInventory.ts):
//
//   Storage breakdown   → measured live from localStorage's truxsocial:* keys.
//   Clear interface state → actually removes the clearable keys and re-measures.
//   Download your data  → the same authService.downloadData flow Account uses
//                         (accessibility-of-discovery: same action, two doors).
//
// Session and preferences are deliberately NOT clearable here: sign-out owns the
// session lifecycle and Appearance → Reset owns preferences. The rows say so.
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Panel, Button, useToast } from '@/ui';
import { useSession } from '@/hooks';
import {
  storageInventory,
  clearInterfaceState,
  totalBytes,
  formatBytes,
  type StorageCategory,
} from './storageInventory';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const RowHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  max-width: 44ch;
`;

const Bytes = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textMuted};
`;

const TotalRow = styled(Row)`
  padding-top: 10px;
  border-top: 1px solid ${({ theme }) => theme.color.hairSoft};
`;

const TotalLabel = styled(RowLabel)`
  font-weight: 600;
`;

const CATEGORY_HINTS: Record<StorageCategory['id'], string> = {
  session: 'Who is signed in. Cleared by signing out.',
  preferences: 'Theme, accent and accessibility choices. Reset under Appearance.',
  interface: 'Collapsed sidebars, dismissed banners and view modes.',
  owner: 'Owner console working data.',
  other: 'Anything else TruXSocial has stored.',
};

export function DataStorageSection() {
  const { downloadData } = useSession();
  const toast = useToast();
  const [inventory, setInventory] = useState(() => storageInventory());

  const refresh = useCallback(() => setInventory(storageInventory()), []);

  function handleClear() {
    const removed = clearInterfaceState();
    refresh();
    toast(
      removed === 0
        ? 'Nothing to clear. Interface state is already empty'
        : `Cleared ${removed} stored ${removed === 1 ? 'item' : 'items'}`,
    );
  }

  async function handleDownload() {
    await downloadData();
    toast('Your data export has started');
  }

  return (
    <Stack>
      <Card>
        <CardTitle>Storage on this device</CardTitle>
        <RowHint>
          Everything TruXSocial keeps in this browser. Sizes are approximate. Posts and media
          live on the server, not on this device.
        </RowHint>
        {inventory.map((cat) => (
          <Row key={cat.id} data-testid={`storage-row-${cat.id}`}>
            <RowText>
              <RowLabel>{cat.label}</RowLabel>
              <RowHint>{CATEGORY_HINTS[cat.id]}</RowHint>
            </RowText>
            <Bytes>{formatBytes(cat.bytes)}</Bytes>
          </Row>
        ))}
        <TotalRow>
          <TotalLabel>Total</TotalLabel>
          <Bytes data-testid="storage-total">{formatBytes(totalBytes(inventory))}</Bytes>
        </TotalRow>
      </Card>

      <Card>
        <CardTitle>Clear data</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Clear interface state</RowLabel>
            <RowHint>
              Forgets collapsed sidebars, dismissed banners and cached working data. Your
              session and preferences are not touched.
            </RowHint>
          </RowText>
          <Button variant="secondary" onClick={handleClear}>
            Clear
          </Button>
        </Row>
      </Card>

      <Card>
        <CardTitle>Your data</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Download your data</RowLabel>
            <RowHint>
              A JSON export of your account record, without your password. Also available
              under Account Settings.
            </RowHint>
          </RowText>
          <Button variant="secondary" onClick={() => void handleDownload()}>
            Download
          </Button>
        </Row>
      </Card>
    </Stack>
  );
}
